/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withTranslation, useTranslation } from 'react-i18next';
import { Accordion, AccordionItem, Select, SelectItem, DataTable } from '@carbon/react';
import { decycle } from 'json-decycle';
import { getTranslatedExtensions } from '../utils/translate';
import DefinitionDisplay from './DefinitionDisplay';
import ExtensionsDisplay from './ExtensionsDisplay';
import Markdown from './Markdown';
import { useApi } from '../contexts/ApiHandler';

const { Table, TableRow, TableBody, TableCell } = DataTable;

function ResponsesDisplay({ operationPath, operationMethod, operation, accept, options }) {
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [responseDescription, setResponseDescription] = useState(null);
  const [responseObject, setResponseObject] = useState(null);
  const [responseObjects, setResponseObjects] = useState(null);
  const [responseExtensions, setResponseExtensions] = useState(null);
  const [responseHeaders, setResponseHeaders] = useState(null);
  const [responseLinks, setResponseLinks] = useState(null);
  const [markupType, setMarkupType] = useState(null);
  const { t } = useTranslation();
  const { api, apiHandler } = useApi();
  useEffect(() => {
    if (operation && operation.responses) {
      setMarkupType(apiHandler.getMarkupType());
      const response = selectedResponse ?? Object.keys(operation.responses).sort()[0];
      let theResponse = operation.responses[response];
      setSelectedResponse(response);

      // dereference the whole response if required
      if (theResponse && theResponse.$ref) {
        theResponse = apiHandler.getLocalReference(theResponse.$ref, api);
      }

      setResponseObjects(operation.responses);
      setResponseDescription(getTranslatedExtensions(theResponse, 'description'));

      let headers = { ...theResponse.headers };
      // dereference the headers if required
      if (headers && headers.$ref) {
        headers = apiHandler.getLocalReference(headers.$ref, api);
      }
      // dereference each header individually if required as well
      if (headers) {
        Object.keys(headers).forEach((headerName) => {
          const header = headers[headerName];
          if (header.$ref) {
            headers[headerName] = apiHandler.getLocalReference(header.$ref, api);
          }
        });
      }
      setResponseHeaders(headers);

      // swagger 2.0
      if (api.swagger) {
        setResponseObject(theResponse);
        setResponseExtensions(apiHandler.getCustomExtensions(responseObject));
      }
      // openapi 3.0
      else {
        setResponseObject(theResponse);
        setResponseExtensions(apiHandler.getCustomExtensions(theResponse));
      }

      const { links } = theResponse;
      // dereference the links if required
      if (links) {
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, val] of Object.entries(links)) {
          if (val.$ref) {
            links[key] = apiHandler.getLocalReference(val.$ref, api);
          }
        }
      }
      setResponseLinks(links);
    }
  }, [accept, api, apiHandler, operation, responseObject, selectedResponse]);

  let maxDepth = 9;
  if (options && typeof options.apiMaxDepth !== 'undefined') {
    maxDepth = options.apiMaxDepth;
  }

  return (
    <section className="responses" aria-label="operation-responses">
      <div className="operationInformation operationSummary responseSelection" data-testid="responseSelection">
        <h3 className="labelText">
          {t('responses')}
        </h3>
        <div className="values">
          {responseObjects && Object.keys(responseObjects).length > 1 && (
            <div className={classNames('responsesContainer', `response-code-${selectedResponse}`)}>
              <div className="responseCode">
                <Select
                  hideLabel
                  id="operationResponses"
                  value={selectedResponse}
                  onChange={(event) => {
                    setSelectedResponse(event.target.value);
                  }}
                >
                  {Object.keys(responseObjects)
                    .sort()
                    .map((response) => (
                      <SelectItem key={response} value={response} text={response} />
                    ))}
                </Select>
              </div>
              {responseDescription && (
                <Markdown className="responseDescription" data-testid="responseDescription" data={responseDescription} markupType={markupType} />
              )}
            </div>
          )}
          {responseObjects && Object.keys(responseObjects).length === 1 && (
            <div className={classNames('responseContainer', `response-code-${selectedResponse}`)}>
              <div className="responseCode">
                <p>{selectedResponse}</p>
              </div>
              {responseDescription && <Markdown className="responseDescription" data={responseDescription} markupType={markupType} />}
            </div>
          )}
        </div>
      </div>
      <Accordion align="start">
        {selectedResponse && responseHeaders && Object.keys(responseHeaders).length > 0 && (
          <div className="operationInformation operationSummary responseHeaders">
            <div className="labelText" />
            <div className="values">
              <AccordionItem title={t('header')} open>
                <Table className="responseHeaderTable">
                  <TableBody>
                    {Object.keys(responseHeaders).map((responseHeader) => {
                      const thisResponseHeader = responseHeaders[responseHeader];
                      return (
                        <TableRow key={responseHeader}>
                          <TableCell className="responseHeaderNameContainer">
                            <div className="responseHeaderName">{responseHeader}</div>
                          </TableCell>
                          <TableCell className="responseHeaderDescriptionContainer">
                            <div className="responseHeaderDescription">{getTranslatedExtensions(thisResponseHeader, 'description')}</div>
                            <DefinitionDisplay
                              pathName={operationPath}
                              method={operationMethod}
                              object={responseHeaders[responseHeader]}
                              contentType={accept}
                              mode="both"
                              maxDepth={maxDepth}
                              viewMode="schema"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </AccordionItem>
            </div>
          </div>
        )}
        {selectedResponse && responseObject && (responseObject.schema || responseObject.content) && (
          <div className="operationInformation operationSummary responseSchema">
            <div className="labelText" />
            <div className="values">
              <AccordionItem title={t('body')} open>
                <DefinitionDisplay
                  type="response"
                  objectIdentifier={selectedResponse}
                  object={responseObject}
                  pathName={operationPath}
                  method={operationMethod}
                  contentType={accept}
                  maxDepth={maxDepth}
                  viewMode="schema"
                />
              </AccordionItem>
            </div>
          </div>
        )}
        {selectedResponse && responseExtensions && (
          <div className="operationInformation operationSummary responseSchema">
            <div className="labelText" />
            <div className="values">
              <AccordionItem title={t('extensions')} open>
                <ExtensionsDisplay label="" extensions={responseExtensions} />
              </AccordionItem>
            </div>
          </div>
        )}
        {selectedResponse && responseLinks && (
          <div className="operationInformation operationSummary responseLinks">
            <div className="labelText" />
            <div className="values">
              <AccordionItem title={t('links')} open>
                <Table className="responseLinkTable">
                  <TableBody>
                    {Object.keys(responseLinks).map((responseLink) => {
                      const thisResponseLink = responseLinks[responseLink];
                      return (
                        <TableRow key={responseLink}>
                          <TableCell className="responseLinkNameContainer">
                            <div className="responseLinkName">{responseLink}</div>
                          </TableCell>
                          <TableCell className="responseLinkContentContainer">
                            {thisResponseLink.description && <div className="responseLinkDescription">{thisResponseLink.description}</div>}
                            {thisResponseLink.error && <pre className="responseLinkError">{`missing reference: ${thisResponseLink.$ref}`}</pre>}
                            {thisResponseLink.operationRef && <pre className="responseLinkOperation">Operation: {thisResponseLink.operationRef}</pre>}
                            {thisResponseLink.operationId && <pre className="responseLinkOperation">Operation: {thisResponseLink.operationId}</pre>}
                            {thisResponseLink.requestBody && (
                              <pre className="responseLinkRequestBody">Request Body: {JSON.stringify(thisResponseLink.requestBody, decycle(), 2)}</pre>
                            )}
                            {thisResponseLink.parameters && (
                              <pre className="responseLinkParameters">Parameters: {JSON.stringify(thisResponseLink.parameters, decycle(), 2)}</pre>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </AccordionItem>
            </div>
          </div>
        )}
      </Accordion>
    </section>
  );
}

ResponsesDisplay.propTypes = {
  api: PropTypes.object,
  operation: PropTypes.object,
  operationPath: PropTypes.string,
  operationMethod: PropTypes.string,
  accept: PropTypes.string,
  options: PropTypes.object
};

export default withTranslation()(ResponsesDisplay);
